import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";

import {
  FiAlertTriangle,
  FiBox,
  FiDollarSign,
  FiPackage,
  FiPlus,
  FiRefreshCcw,
} from "react-icons/fi";

import API from "../../../../../../utils/apiCalling";
import { config } from "../../../../../../utils/apiUrl";
import useSwal from "../../../../../../hooks/useSwal";

import ProductFilters from "./ProductFilters";
import ProductTable from "./ProductTable";
import AddEditProduct from "./AddEditProduct";
import ProductDrawer from "./ProductDrawer";

import StockAdjustmentDialog from "../Stock/StockAdjustmentDialog";

import "./ProductManagement.css";


/* =========================================================
   FILTERS
========================================================= */

const EMPTY_FILTERS = {
  search: "",
  status: "",
  category: "",
  subCategory: "",
  brand: "",
  condition: "",
  productType: "",
  stockStatus: "",
  minPrice: "",
  maxPrice: "",
  isFeatured: "",
  sortBy: "createdAt",
  sortOrder: "desc",
};


/* =========================================================
   PAGINATION
========================================================= */

const EMPTY_PAGINATION = {
  total: 0,
  page: 1,
  limit: 10,
  totalPages: 1,
};


/* =========================================================
   SUMMARY
========================================================= */

const EMPTY_SUMMARY = {
  totalProducts: 0,
  activeProducts: 0,
  inactiveProducts: 0,
  draftProducts: 0,

  outOfStock: 0,
  lowStockProducts: 0,

  totalStockQty: 0,
  totalPurchaseValue: 0,
  totalSellingValue: 0,
};


/* =========================================================
   FORMAT CURRENCY
========================================================= */

const formatCurrency = (
  value
) => {
  return new Intl.NumberFormat(
    "en-IN",
    {
      style:
        "currency",

      currency:
        "INR",

      maximumFractionDigits:
        0,
    }
  ).format(
    Number(
      value ||
      0
    )
  );
};


/* =========================================================
   FORMAT NUMBER
========================================================= */

const formatNumber = (
  value
) => {
  return new Intl.NumberFormat(
    "en-IN"
  ).format(
    Number(
      value ||
      0
    )
  );
};


/* =========================================================
   COMPONENT
========================================================= */

const ProductManagement = () => {
  const api =
    useMemo(
      () =>
        new API(),
      []
    );

  const swal =
    useSwal();


  /* =======================================================
     PRODUCTS
  ======================================================= */

  const [
    products,
    setProducts,
  ] =
    useState([]);


  /* =======================================================
     FILTERS
  ======================================================= */

  const [
    filters,
    setFilters,
  ] =
    useState({
      ...EMPTY_FILTERS,
    });


  const [
    appliedFilters,
    setAppliedFilters,
  ] =
    useState({
      ...EMPTY_FILTERS,
    });


  /* =======================================================
     PAGINATION
  ======================================================= */

  const [
    pagination,
    setPagination,
  ] =
    useState({
      ...EMPTY_PAGINATION,
    });


  /* =======================================================
     SUMMARY
  ======================================================= */

  const [
    summary,
    setSummary,
  ] =
    useState({
      ...EMPTY_SUMMARY,
    });


  /* =======================================================
     LOADING
  ======================================================= */

  const [
    loading,
    setLoading,
  ] =
    useState(false);


  /* =======================================================
     ERROR
  ======================================================= */

  const [
    error,
    setError,
  ] =
    useState("");


  /* =======================================================
     ADD / EDIT PRODUCT
  ======================================================= */

  const [
    formOpen,
    setFormOpen,
  ] =
    useState(false);


  const [
    editingProduct,
    setEditingProduct,
  ] =
    useState(null);


  /* =======================================================
     PRODUCT DETAILS DRAWER
  ======================================================= */

  const [
    selectedProduct,
    setSelectedProduct,
  ] =
    useState(null);


  /* =======================================================
     STOCK ADJUSTMENT PRODUCT

     This is the product passed into:

     Stock/StockAdjustmentDialog.jsx
  ======================================================= */

  const [
    stockProduct,
    setStockProduct,
  ] =
    useState(null);


  /* =======================================================
     FETCH PRODUCTS
  ======================================================= */

  const fetchProducts =
    useCallback(
      async () => {
        try {
          setLoading(
            true
          );

          setError(
            ""
          );


          const payload = {
            page:
              pagination.page,

            limit:
              pagination.limit,


            search:
              appliedFilters
                .search
                ?.trim() ||
              "",


            status:
              appliedFilters
                .status ||
              "",


            category:
              appliedFilters
                .category ||
              "",


            subCategory:
              appliedFilters
                .subCategory ||
              "",


            brand:
              appliedFilters
                .brand
                ?.trim() ||
              "",


            condition:
              appliedFilters
                .condition ||
              "",


            productType:
              appliedFilters
                .productType ||
              "",


            stockStatus:
              appliedFilters
                .stockStatus ||
              "",


            minPrice:
              appliedFilters
                .minPrice ||
              "",


            maxPrice:
              appliedFilters
                .maxPrice ||
              "",


            isFeatured:
              appliedFilters
                .isFeatured,


            sortBy:
              appliedFilters
                .sortBy ||
              "createdAt",


            sortOrder:
              appliedFilters
                .sortOrder ||
              "desc",
          };


          const response =
            await api.post(
              config
                .getInventoryProducts,

              payload
            );


          /* =================================================
             SUCCESS
          ================================================= */

          if (
            response?.status ===
            "success"
          ) {
            const records =
              Array.isArray(
                response?.data
              )
                ? response.data
                : [];


            setProducts(
              records
            );


            setPagination(
              (
                previous
              ) => ({
                ...previous,


                total:
                  Number(
                    response
                      ?.pagination
                      ?.total ||
                    0
                  ),


                page:
                  Number(
                    response
                      ?.pagination
                      ?.page ||
                    previous.page
                  ),


                limit:
                  Number(
                    response
                      ?.pagination
                      ?.limit ||
                    previous.limit
                  ),


                totalPages:
                  Math.max(
                    Number(
                      response
                        ?.pagination
                        ?.totalPages ||
                      1
                    ),

                    1
                  ),
              })
            );


            setSummary({
              ...EMPTY_SUMMARY,

              ...(response
                ?.summary ||
                {}),
            });


            return;
          }


          /* =================================================
             FAILED RESPONSE
          ================================================= */

          setProducts(
            []
          );


          setPagination(
            (
              previous
            ) => ({
              ...previous,

              total:
                0,

              totalPages:
                1,
            })
          );


          setSummary({
            ...EMPTY_SUMMARY,
          });


          setError(
            response?.message ||
              "Unable to fetch products."
          );

        } catch (
          requestError
        ) {
          console.error(
            "Get inventory products error:",
            requestError
          );


          setProducts(
            []
          );


          setPagination(
            (
              previous
            ) => ({
              ...previous,

              total:
                0,

              totalPages:
                1,
            })
          );


          setSummary({
            ...EMPTY_SUMMARY,
          });


          setError(
            requestError
              ?.response
              ?.data
              ?.message ||
            requestError
              ?.message ||
            "Unable to fetch products."
          );

        } finally {
          setLoading(
            false
          );
        }
      },
      [
        api,
        appliedFilters,
        pagination.page,
        pagination.limit,
      ]
    );


  /* =======================================================
     INITIAL / FILTER / PAGE LOAD
  ======================================================= */

  useEffect(
    () => {
      fetchProducts();
    },
    [
      fetchProducts,
    ]
  );


  /* =======================================================
     SEARCH
  ======================================================= */

  const handleSearch =
    () => {
      setAppliedFilters({
        ...filters,
      });


      setPagination(
        (
          previous
        ) => ({
          ...previous,

          page:
            1,
        })
      );
    };


  /* =======================================================
     RESET FILTERS
  ======================================================= */

  const handleReset =
    () => {
      setFilters({
        ...EMPTY_FILTERS,
      });


      setAppliedFilters({
        ...EMPTY_FILTERS,
      });


      setPagination(
        (
          previous
        ) => ({
          ...previous,

          page:
            1,
        })
      );
    };


  /* =======================================================
     ADD PRODUCT
  ======================================================= */

  const handleAddProduct =
    () => {
      setEditingProduct(
        null
      );

      setFormOpen(
        true
      );
    };


  /* =======================================================
     EDIT PRODUCT
  ======================================================= */

  const handleEditProduct =
    (
      product
    ) => {
      setEditingProduct(
        product
      );

      setFormOpen(
        true
      );
    };


  /* =======================================================
     VIEW PRODUCT
  ======================================================= */

  const handleViewProduct =
    (
      product
    ) => {
      setSelectedProduct(
        product
      );
    };


  /* =======================================================
     ADJUST PRODUCT STOCK

     Opens the shared StockAdjustmentDialog.
  ======================================================= */

  const handleAdjustStock =
    (
      product
    ) => {
      setStockProduct(
        product
      );
    };


  /* =======================================================
     DELETE PRODUCT
  ======================================================= */

  const handleDeleteProduct =
    async (
      product
    ) => {
      const confirmation =
        await swal.warning({
          icon:
            "warning",

          title:
            "Delete product?",

          html: `
            <div style="text-align:center">
              <p style="margin:0 0 8px">
                Are you sure you want to delete
                <strong>${product?.name || "this product"}</strong>?
              </p>

              <small style="color:#667085">
                The product will be soft deleted and removed from the active inventory list.
              </small>
            </div>
          `,

          showCancelButton:
            true,

          confirmButtonText:
            "Yes, Delete",

          cancelButtonText:
            "Cancel",

          confirmButtonColor:
            "#dc2626",
        });


      if (
        !confirmation
          .isConfirmed
      ) {
        return;
      }


      try {
        const response =
          await api.delete(
            config
              .deleteInventoryProduct(
                product._id
              )
          );


        if (
          response?.status ===
          "success"
        ) {
          await swal.success({
            icon:
              "success",

            title:
              "Product Deleted",

            text:
              response?.message ||
              "Product deleted successfully.",

            timer:
              1400,

            showConfirmButton:
              false,
          });


          if (
            products.length ===
              1 &&
            pagination.page >
              1
          ) {
            setPagination(
              (
                previous
              ) => ({
                ...previous,

                page:
                  previous.page -
                  1,
              })
            );

          } else {
            fetchProducts();
          }


          return;
        }


        throw new Error(
          response?.message ||
            "Unable to delete product."
        );

      } catch (
        requestError
      ) {
        swal.error({
          icon:
            "error",

          title:
            "Delete Failed",

          text:
            requestError
              ?.response
              ?.data
              ?.message ||
            requestError
              ?.message ||
            "Unable to delete product.",
        });
      }
    };


  /* =======================================================
     CHANGE PRODUCT STATUS
  ======================================================= */

  const handleChangeStatus =
    async (
      product,
      nextStatus
    ) => {
      try {
        const response =
          await api.patch(
            config
              .changeInventoryProductStatus(
                product._id
              ),

            {
              status:
                nextStatus,
            }
          );


        if (
          response?.status ===
          "success"
        ) {
          await swal.success({
            icon:
              "success",

            title:
              "Status Updated",

            text:
              response?.message ||
              "Product status updated successfully.",

            timer:
              1300,

            showConfirmButton:
              false,
          });


          fetchProducts();


          return;
        }


        throw new Error(
          response?.message ||
            "Unable to update product status."
        );

      } catch (
        requestError
      ) {
        swal.error({
          icon:
            "error",

          title:
            "Update Failed",

          text:
            requestError
              ?.response
              ?.data
              ?.message ||
            requestError
              ?.message ||
            "Unable to update product status.",
        });
      }
    };


  /* =======================================================
     PRODUCT FORM SUCCESS
  ======================================================= */

  const handleFormSuccess =
    (
      savedProduct
    ) => {
      setFormOpen(
        false
      );


      setEditingProduct(
        null
      );


      if (
        savedProduct?._id
      ) {
        setSelectedProduct(
          savedProduct
        );
      }


      fetchProducts();
    };


  /* =======================================================
     PAGE CHANGE
  ======================================================= */

  const handlePageChange =
    (
      page
    ) => {
      if (
        page <
          1 ||
        page >
          pagination.totalPages
      ) {
        return;
      }


      setPagination(
        (
          previous
        ) => ({
          ...previous,

          page,
        })
      );
    };


  /* =======================================================
     LIMIT CHANGE
  ======================================================= */

  const handleLimitChange =
    (
      event
    ) => {
      setPagination(
        (
          previous
        ) => ({
          ...previous,

          page:
            1,

          limit:
            Number(
              event
                .target
                .value
            ),
        })
      );
    };


  /* =======================================================
     SUMMARY CARDS
  ======================================================= */

  const summaryCards = [
    {
      key:
        "total-products",

      title:
        "Total Products",

      value:
        formatNumber(
          summary
            .totalProducts
        ),

      subtitle:
        `${formatNumber(
          summary
            .activeProducts
        )} active`,

      icon:
        FiPackage,

      className:
        "inventory-product-summary-blue",
    },


    {
      key:
        "total-stock",

      title:
        "Total Stock",

      value:
        formatNumber(
          summary
            .totalStockQty
        ),

      subtitle:
        "Total inventory units",

      icon:
        FiBox,

      className:
        "inventory-product-summary-purple",
    },


    {
      key:
        "low-stock",

      title:
        "Low Stock",

      value:
        formatNumber(
          summary
            .lowStockProducts
        ),

      subtitle:
        `${formatNumber(
          summary
            .outOfStock
        )} out of stock`,

      icon:
        FiAlertTriangle,

      className:
        "inventory-product-summary-red",
    },


    {
      key:
        "purchase-value",

      title:
        "Purchase Value",

      value:
        formatCurrency(
          summary
            .totalPurchaseValue
        ),

      subtitle:
        "Current stock cost",

      icon:
        FiDollarSign,

      className:
        "inventory-product-summary-orange",
    },


    {
      key:
        "selling-value",

      title:
        "Selling Value",

      value:
        formatCurrency(
          summary
            .totalSellingValue
        ),

      subtitle:
        "Estimated inventory value",

      icon:
        FiDollarSign,

      className:
        "inventory-product-summary-green",
    },
  ];


  /* =======================================================
     RENDER
  ======================================================= */

  return (
    <div
      className="inventory-product-page"
    >
      {/* ===================================================
          HEADER
      =================================================== */}

      <div
        className="inventory-product-header"
      >
        <div>
          <span>
            PRODUCT MANAGEMENT
          </span>


          <h3>
            Inventory Products
          </h3>


          <p>
            Add and manage products,
            pricing, categories, images and
            inventory details.
          </p>
        </div>


        <div
          className="inventory-product-header-actions"
        >
          <button
            type="button"
            className="inventory-product-refresh"
            onClick={
              fetchProducts
            }
            disabled={
              loading
            }
            title="Refresh products"
          >
            <FiRefreshCcw
              className={
                loading
                  ? "spinning"
                  : ""
              }
            />
          </button>


          <button
            type="button"
            className="inventory-product-add"
            onClick={
              handleAddProduct
            }
          >
            <FiPlus />

            Add Product
          </button>
        </div>
      </div>


      {/* ===================================================
          SUMMARY
      =================================================== */}

      <div
        className="inventory-product-summary-grid"
      >
        {summaryCards.map(
          (
            card
          ) => {
            const Icon =
              card.icon;


            return (
              <div
                key={
                  card.key
                }
                className={`inventory-product-summary-card ${card.className}`}
              >
                <div
                  className="inventory-product-summary-icon"
                >
                  <Icon />
                </div>


                <div
                  className="inventory-product-summary-content"
                >
                  <span>
                    {
                      card.title
                    }
                  </span>


                  <strong>
                    {loading
                      ? "..."
                      : card.value}
                  </strong>


                  <small>
                    {
                      card.subtitle
                    }
                  </small>
                </div>
              </div>
            );
          }
        )}
      </div>


      {/* ===================================================
          FILTER
      =================================================== */}

      <ProductFilters
        filters={
          filters
        }

        setFilters={
          setFilters
        }

        loading={
          loading
        }

        onSearch={
          handleSearch
        }

        onReset={
          handleReset
        }
      />


      {/* ===================================================
          ERROR
      =================================================== */}

      {error ? (
        <div
          className="inventory-product-error"
        >
          {error}
        </div>
      ) : null}


      {/* ===================================================
          PRODUCT TABLE
      =================================================== */}

      <ProductTable
        products={
          products
        }

        loading={
          loading
        }

        pagination={
          pagination
        }

        onView={
          handleViewProduct
        }

        onEdit={
          handleEditProduct
        }

        onAdjustStock={
          handleAdjustStock
        }

        onDelete={
          handleDeleteProduct
        }

        onChangeStatus={
          handleChangeStatus
        }

        onPageChange={
          handlePageChange
        }

        onLimitChange={
          handleLimitChange
        }

        onAddProduct={
          handleAddProduct
        }
      />


      {/* ===================================================
          ADD / EDIT PRODUCT
      =================================================== */}

      <AddEditProduct
        open={
          formOpen
        }

        product={
          editingProduct
        }

        onClose={() => {
          setFormOpen(
            false
          );

          setEditingProduct(
            null
          );
        }}

        onSuccess={
          handleFormSuccess
        }
      />


      {/* ===================================================
          SHARED STOCK ADJUSTMENT DIALOG

          IMPORTANT:

          This is Stock/StockAdjustmentDialog.jsx.

          We are NOT using:
          Products/StockAdjustmentModal.jsx
      =================================================== */}

      <StockAdjustmentDialog
        open={
          Boolean(
            stockProduct
          )
        }

        product={
          stockProduct
        }

        products={
          products
        }

        onClose={() => {
          setStockProduct(
            null
          );
        }}

        onSuccess={async (
          data
        ) => {
          /*
           * Close stock dialog.
           */

          setStockProduct(
            null
          );


          /*
           * If the product drawer is currently
           * displaying the same product, update it
           * immediately with returned product data.
           */

          if (
            data?.product
              ?._id
          ) {
            setSelectedProduct(
              (
                current
              ) => {
                if (
                  String(
                    current
                      ?._id ||
                    ""
                  ) ===
                  String(
                    data
                      .product
                      ._id
                  )
                ) {
                  return data
                    .product;
                }


                return current;
              }
            );
          }


          /*
           * Reload products + summary cards so
           * inventory values are immediately refreshed.
           */

          await fetchProducts();
        }}
      />


      {/* ===================================================
          PRODUCT DRAWER
      =================================================== */}

      <ProductDrawer
        open={
          Boolean(
            selectedProduct
          )
        }

        product={
          selectedProduct
        }

        onClose={() =>
          setSelectedProduct(
            null
          )
        }

        onEdit={(
          product
        ) => {
          setSelectedProduct(
            null
          );

          handleEditProduct(
            product
          );
        }}

        onRefresh={() => {
          fetchProducts();
        }}
      />
    </div>
  );
};


export default ProductManagement;